// "Download this view" for a plain `<table>`: the RENDERED table, serialized.
//
// WHY IT READS THE DOM AND NOT THE ROWS BEHIND IT. Every table in the admin
// console is hand-written markup over its own record type (`table.css` states
// the house rule: no heavyweight <Table> component wraps it), and the cell an
// operator reads is almost never the field the row carries — it is a formatted
// date, a derived title, a rate with an unknown-denominator dash, a badge whose
// word is chosen in JSX. A typed exporter per table would mean ~40 header lists
// and ~40 row mappers, each free to drift from the column beside it the moment
// someone changes a cell. Reading the rendered table cannot drift: it exports
// what is on the screen, in the order it is on the screen, under whatever
// filter, sort and Show more window the operator already chose. That is the
// same promise `admin/lib/experimentation.ts::registerCsv` makes in prose for
// the one table that had an export before this; this generalises it.
//
// The trade, stated plainly: a column that prints "-" for unknown exports "-",
// not an empty cell, because that is what the reader saw. A number formatted
// with a thousands separator exports with it. This file does NOT try to undo
// presentation — a spreadsheet of what was on the screen is the deliverable.
//
// What it must never do is export something the operator could NOT see, so the
// walk skips `aria-hidden` subtrees and anything marked `data-csv-ignore`.

/** Columns that carry controls rather than data. Excluded by class, because
 *  `.rc-actions-col` is already the console's marker for exactly that. */
const ACTION_COLUMN_CLASS = "rc-actions-col";

/** Opt-out for a header, a row, or an element inside a cell. */
const IGNORE_ATTR = "data-csv-ignore";

/** RFC 4180 quoting: wrap every cell and double any quote inside it. Applied to
 *  EVERY cell rather than the ones that look risky, because free text is what
 *  breaks a file and the row that breaks it is the one nobody predicted. */
export function csvCell(value: string | number | null | undefined): string {
  const text = value === null || value === undefined ? "" : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

/** A stable, dated name so two exports never overwrite each other in a
 *  downloads folder. Matches the shape the experiment register already used. */
export function csvFilename(stem: string, now: Date): string {
  return `${stem}-${now.toISOString().slice(0, 10)}.csv`;
}

function isElement(node: Node): node is Element {
  return node.nodeType === 1;
}

/** The text of one node as a reader sees it. Element children are joined with a
 *  space rather than concatenated, because a cell that stacks a strong line
 *  over a muted one (`.rc-cell-strong` + `.rc-cell-sub`) would otherwise export
 *  as "AcmeAcme Corp". */
function nodeText(node: Node): string {
  if (node.nodeType === 3) return node.nodeValue ?? "";
  if (!isElement(node)) return "";
  if (node.getAttribute("aria-hidden") === "true") return "";
  if (node.hasAttribute(IGNORE_ATTR)) return "";
  const parts: string[] = [];
  for (const child of Array.from(node.childNodes)) parts.push(nodeText(child));
  return parts.filter((p) => p !== "").join(" ");
}

/** One cell, whitespace collapsed. A CSV cell holding the newlines and indent
 *  of the JSX that produced it is a cell nobody can filter on. */
function cellText(cell: Element): string {
  return nodeText(cell).replace(/\s+/g, " ").trim();
}

/** The header cells, one entry per COLUMN: a `colSpan` header contributes its
 *  label once per column it covers, so header and body stay index-aligned when
 *  a table groups its columns. */
function headerCells(row: HTMLTableRowElement): Element[] {
  const out: Element[] = [];
  for (const cell of Array.from(row.cells)) {
    for (let i = 0; i < Math.max(1, cell.colSpan); i += 1) out.push(cell);
  }
  return out;
}

function isExported(cell: Element): boolean {
  return !cell.classList.contains(ACTION_COLUMN_CLASS) && !cell.hasAttribute(IGNORE_ATTR);
}

/** The rendered table as CSV text, or "" when there is nothing to export.
 *
 *  The LAST `<thead>` row names the columns: a table with a grouping row above
 *  its real headers would otherwise export the group labels and lose the leaf
 *  ones.
 *
 *  A body row is data only if it has one cell per column and no cell spans
 *  several. That single rule drops every non-record row the console renders
 *  inside a tbody - the Show more control, the empty state, the loading
 *  skeleton, a "sorted within the fetched pages" note - without any of them
 *  having to know this file exists. */
export function tableToCsv(table: HTMLTableElement): string {
  const headRows = table.tHead ? Array.from(table.tHead.rows) : [];
  const headRow = headRows[headRows.length - 1];
  if (!headRow) return "";

  const columns = headerCells(headRow);
  const keep: number[] = [];
  columns.forEach((cell, index) => {
    if (isExported(cell)) keep.push(index);
  });
  if (keep.length === 0) return "";

  const lines = [keep.map((i) => csvCell(cellText(columns[i]))).join(",")];
  for (const body of Array.from(table.tBodies)) {
    for (const row of Array.from(body.rows)) {
      if (row.hasAttribute(IGNORE_ATTR)) continue;
      const cells = Array.from(row.cells);
      if (cells.length !== columns.length) continue;
      if (cells.some((c) => c.colSpan > 1)) continue;
      lines.push(keep.map((i) => csvCell(cellText(cells[i]))).join(","));
    }
  }
  // Header only means every row was a control row or the table is empty. An
  // empty file is a worse answer than no file, so the caller can skip it.
  return lines.length > 1 ? lines.join("\n") : "";
}

/** The bytes to save. The BOM is not decoration: without it Excel reads a UTF-8
 *  CSV as the local ANSI codepage, and a console full of talent names, country
 *  names and free-text notes is exactly the data that turns to mojibake. */
export function csvBlob(csv: string): Blob {
  // Built from a code point rather than written as a literal: the character is
  // invisible in an editor and a formatter has already turned the escape into
  // one raw byte once, which then trips the no-irregular-whitespace lint.
  const bom = String.fromCharCode(0xfeff);
  return new Blob([bom + csv], { type: "text/csv;charset=utf-8" });
}
